import { useState, useEffect } from "react";
import { Sun } from "lucide-react";

const CountdownGlass = () => {
  const targetDate = new Date("2026-08-12T19:30:00").getTime();
  
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const timeUnits = [
    { value: timeLeft.days, label: "Días" },
    { value: timeLeft.hours, label: "Hrs" },
    { value: timeLeft.minutes, label: "Min" },
    { value: timeLeft.seconds, label: "Seg" }
  ];

  return (
    <section 
      className="relative py-12 sm:py-16 md:py-24 overflow-hidden"
      data-testid="countdown-section"
    >
      {/* Eclipse Gradient Background */}
      <div className="absolute inset-0 bg-eclipse-gradient" />
      
      {/* Sunrise/Eclipse Glow Effect */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] md:w-[600px] md:h-[600px] rounded-full bg-gradient-radial from-amber-500/20 via-orange-500/10 to-transparent blur-3xl" />
      </div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-3 sm:px-4">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 glass-countdown px-4 py-2 rounded-full mb-4 sm:mb-6">
            <Sun className="w-4 h-4 text-[#F7A81B]" />
            <span className="text-[#F7A81B] text-xs sm:text-sm font-heading uppercase tracking-widest">
              Eclipse Solar Total
            </span>
          </div>
          <h2 className="font-accent text-xl sm:text-2xl md:text-4xl lg:text-5xl text-white mb-2">
            12 de Agosto de 2026
          </h2>
          <p className="text-slate-400 text-sm md:text-base">
            Zaragoza en la Red de Relojes del Eclipse
          </p>
        </div>

        {/* Countdown Numbers with Glassmorphism - Mobile Optimized */}
        <div 
          className="grid grid-cols-4 gap-2 sm:gap-3 md:gap-6 max-w-md sm:max-w-lg md:max-w-2xl mx-auto"
          data-testid="countdown-timer"
        >
          {timeUnits.map((unit, index) => (
            <div 
              key={unit.label}
              className="text-center"
              data-testid={`countdown-${unit.label.toLowerCase()}`}
            >
              <div className="glass-countdown rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-6 relative overflow-hidden group aspect-square flex items-center justify-center">
                {/* Inner Glow */}
                <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Number - Responsive sizing */}
                <span className="relative z-10 text-2xl sm:text-4xl md:text-6xl lg:text-7xl font-bold-sans text-transparent bg-clip-text bg-gradient-to-b from-white via-slate-200 to-slate-400 block leading-none">
                  {String(unit.value).padStart(2, '0')}
                </span>
              </div>
              <span className="text-slate-400 text-[10px] sm:text-xs md:text-sm uppercase tracking-wider mt-1 sm:mt-2 block font-heading">
                {unit.label}
              </span>
            </div>
          ))}
        </div>

        {/* Eclipse Info */}
        <div className="mt-8 sm:mt-10 text-center">
          <div className="inline-flex items-center gap-2 sm:gap-3 glass-countdown px-4 sm:px-6 py-2 sm:py-3 rounded-full">
            <span className="relative flex h-2 w-2 sm:h-3 sm:w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F7A81B] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 sm:h-3 sm:w-3 bg-[#F7A81B]" />
            </span>
            <span className="text-slate-300 text-xs sm:text-sm md:text-base font-heading">
              Oscuridad máxima: 19:30h
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CountdownGlass;
